// ============================================================
// FloraIQ - Metodologia FloraIQ-LADM: conteudo conceitual
// ============================================================
// Movido de src/mocks/ladm.ts. So documentacao do metodo: conceitos,
// fluxo, tipos de unidade/fonte/RRR, regras de validacao e produtos
// previstos. Exemplos com codigos, nomes de pessoas, datas, contagens e
// percentuais foram retirados por serem ficticios.

export interface LadmGroupDef {
  id: string;
  icon: string;
  title: string;
  description: string;
}

export const ladmOverviewGroups: LadmGroupDef[] = [
  {
    id: "cadastro-territorial",
    icon: "ri-map-pin-line",
    title: "Cadastro Territorial",
    description: "Organiza as unidades espaciais onde os exemplares arbóreos estão inseridos, como município, bairro, logradouro, praça, parque, canteiro, lote, área verde ou UC.",
  },
  {
    id: "cadastro-arboreo",
    icon: "ri-plant-line",
    title: "Cadastro Arbóreo",
    description: "Registra cada exemplar arbóreo individualmente, com código público, coordenada, espécie, medidas, fitossanidade, risco, interferências e manejo recomendado.",
  },
  {
    id: "relacoes-administrativas",
    icon: "ri-building-line",
    title: "Relações Administrativas",
    description: "Relaciona exemplares e áreas a responsáveis, órgãos, empresas executoras, concessionárias, autorizações, permissões e restrições.",
  },
  {
    id: "fontes-evidencias",
    icon: "ri-folder-info-line",
    title: "Fontes e Evidências",
    description: "Vincula cada informação relevante a uma fonte técnica, como vistoria, foto, laudo, ortofoto, nuvem de pontos, lei, autorização ou ordem de serviço.",
  },
  {
    id: "historico-monitoramento",
    icon: "ri-history-line",
    title: "Histórico e Monitoramento",
    description: "Registra alterações, vistorias, podas, supressões, replantios, tratamentos, eventos de risco e mudanças de status ao longo do tempo.",
  },
];

export interface LadmMatrixRow {
  conceitoLadm: string;
  adaptacaoFloraIQ: string;
  moduloRelacionado: string;
}

export const ladmMatrixRows: LadmMatrixRow[] = [
  {
    conceitoLadm: "Party / Parte",
    adaptacaoFloraIQ: "Prefeitura, secretaria, técnico, empresa executora, concessionária, cidadão solicitante, órgão ambiental",
    moduloRelacionado: "Partes Envolvidas",
  },
  {
    conceitoLadm: "Spatial Unit / Unidade Espacial",
    adaptacaoFloraIQ: "Exemplar arbóreo individual, canteiro, praça, parque, lote, quadra, logradouro, área verde, UC, APP urbana, bairro, município",
    moduloRelacionado: "Ambiental / Áreas Verdes",
  },
  {
    conceitoLadm: "RRR (Rights, Responsibilities, Restrictions)",
    adaptacaoFloraIQ: "Autorização de poda/supressão, restrição ambiental, responsabilidade de manutenção, obrigação de compensação, permissão de transplante",
    moduloRelacionado: "Responsabilidades e Restrições",
  },
  {
    conceitoLadm: "Source / Fonte Documental",
    adaptacaoFloraIQ: "Vistoria técnica, foto georreferenciada, laudo, ortofoto de drone, nuvem de pontos LiDAR, legislação, ordem de serviço, processo administrativo",
    moduloRelacionado: "Documentos e Fontes",
  },
  {
    conceitoLadm: "Administrative Unit / Unidade Administrativa",
    adaptacaoFloraIQ: "Ordem de serviço, processo administrativo, plano de manejo, autorização ambiental, licença, contrato",
    moduloRelacionado: "Documentos e Fontes",
  },
  {
    conceitoLadm: "Versioned Object / Objeto Versionado",
    adaptacaoFloraIQ: "Histórico de alterações do exemplar: cadastro inicial, vistorias, podas, supressões, replantios, mudanças de risco e status",
    moduloRelacionado: "CTA / FCA",
  },
  {
    conceitoLadm: "Survey / Levantamento",
    adaptacaoFloraIQ: "Coleta de campo com GPS/GNSS, drone, ortomosaico, LiDAR, laser scanner, nuvem de pontos, fotos 360°",
    moduloRelacionado: "Coletor de Campo / Mapa",
  },
];

export interface LadmFlowStepDef {
  step: number;
  title: string;
  icon: string;
  activities: string[];
}

export const ladmOperationalFlow: LadmFlowStepDef[] = [
  {
    step: 1,
    title: "Planejamento",
    icon: "ri-road-map-line",
    activities: [
      "Definir área de estudo e perímetro do inventário",
      "Definir unidades espaciais (bairro, logradouro, praça, parque)",
      "Preparar base cartográfica e shapefiles",
      "Definir formulário de coleta e atributos",
      "Definir critérios técnicos (DAP mínimo, tipos de indivíduo)",
      "Organizar equipe, equipamentos e cronograma",
      "Definir padrão de codificação dos exemplares (código CTA)",
    ],
  },
  {
    step: 2,
    title: "Levantamento Base",
    icon: "ri-flight-takeoff-line",
    activities: [
      "Planejamento de voo e captura com drone",
      "Geração de ortomosaico georreferenciado",
      "Captura LiDAR ou laser scanner terrestre",
      "Processamento de nuvem de pontos",
      "Geração de camadas geográficas (MDT, MDS, NDVI)",
      "Pré-identificação de exemplares por fotointerpretação",
    ],
  },
  {
    step: 3,
    title: "Cadastro de Campo",
    icon: "ri-user-location-line",
    activities: [
      "Localizar o exemplar em campo com GPS",
      "Coletar coordenada precisa (GNSS RTK ou GPS diferencial)",
      "Registrar dados botânicos (espécie, família, origem)",
      "Registrar dados dendrométricos (DAP, altura, copa)",
      "Avaliar fitossanidade e registrar problemas",
      "Registrar interferências (rede elétrica, calçada, edificação)",
      "Capturar fotos do exemplar e do entorno",
      "Recomendar manejo quando aplicável",
    ],
  },
  {
    step: 4,
    title: "Processamento 3D",
    icon: "ri-stack-line",
    activities: [
      "Processar nuvem de pontos LiDAR",
      "Classificar vegetação, solo e edificações",
      "Separar pontos de solo, vegetação e construções",
      "Extrair altura total e diâmetro da copa por exemplar",
      "Gerar métricas 3D (volume de copa, área de projeção)",
      "Relacionar métricas 3D com os exemplares cadastrados",
    ],
  },
  {
    step: 5,
    title: "Modelagem LADM-FloraIQ",
    icon: "ri-node-tree",
    activities: [
      "Relacionar cada exemplar à unidade espacial correspondente",
      "Vincular fontes documentais a cada informação técnica",
      "Registrar responsabilidades (quem plantou, quem mantém)",
      "Registrar restrições (APP, tombamento, espécie protegida)",
      "Registrar documentos vinculados (autorizações, laudos)",
      "Registrar eventos iniciais de cada exemplar",
      "Criar histórico versionado para cada exemplar",
    ],
  },
  {
    step: 6,
    title: "Validação",
    icon: "ri-check-double-line",
    activities: [
      "Verificar duplicidades de exemplares cadastrados",
      "Verificar coordenadas (fora do perímetro, sobrepostas)",
      "Verificar campos obrigatórios preenchidos",
      "Verificar fotos anexadas e qualidade",
      "Conferir exemplares sem unidade espacial vinculada",
      "Conferir exemplares sem fonte documental",
      "Validar exemplares de risco alto (foto + laudo)",
      "Revisar inconsistências técnicas e correções",
    ],
  },
  {
    step: 7,
    title: "Publicação e Gestão",
    icon: "ri-global-line",
    activities: [
      "Publicar mapa arbóreo interativo",
      "Gerar dashboard de indicadores",
      "Gerar relatórios (executivo, técnico, por bairro)",
      "Criar camadas temáticas (risco, espécie, fitossanidade)",
      "Publicar painel de risco público",
      "Publicar painel de recomendações de manejo",
      "Disponibilizar indicadores para gestão municipal",
      "Preparar dados para Portal Público",
    ],
  },
];

/** Tipos de unidade espacial a que um exemplar pode ser vinculado. */
export const ladmSpatialUnitTypes = [
  "Município", "Distrito", "Bairro", "Setor", "Logradouro",
  "Trecho de via", "Quadra", "Lote", "Praça", "Parque",
  "Canteiro central", "Área verde", "Área institucional",
  "Unidade escolar", "Cemitério", "Área de preservação", "Unidade de Conservação",
];

/** Tipos de fonte documental aceitos como evidencia. */
export const ladmSourceTypes = [
  "Vistoria de campo",
  "Foto do exemplar",
  "Ortofoto de drone",
  "Nuvem de pontos",
  "Laser scanner",
  "Laudo técnico",
  "Parecer ambiental",
  "Processo administrativo",
  "Ordem de serviço",
  "Lei ou decreto municipal",
  "Solicitação do cidadão",
  "Registro de manutenção",
];

/** Perguntas que a rastreabilidade deve responder para cada informacao. */
export const ladmTraceabilityQuestions = [
  { question: "Quem informou?", icon: "ri-user-3-line" },
  { question: "Quando informou?", icon: "ri-calendar-check-line" },
  { question: "Como foi medido?", icon: "ri-ruler-line" },
  { question: "Qual equipamento foi usado?", icon: "ri-gps-line" },
  { question: "Qual documento comprova?", icon: "ri-file-text-line" },
  { question: "Qual técnico validou?", icon: "ri-shield-check-line" },
  { question: "Qual versão estava vigente?", icon: "ri-git-branch-line" },
];

export interface LadmRrrTypeDef {
  tipo: "Responsabilidade" | "Restrição" | "Autorização";
  icon: string;
  descricao: string;
  categorias: string[];
}

export const ladmRrrTypes: LadmRrrTypeDef[] = [
  {
    tipo: "Responsabilidade",
    icon: "ri-user-star-line",
    descricao: "Quem responde por gerir, executar, vistoriar ou manter o exemplar ou a área.",
    categorias: ["Gestão do inventário", "Execução do inventário", "Vistoria técnica", "Manutenção de praças", "Solicitação cidadão"],
  },
  {
    tipo: "Restrição",
    icon: "ri-forbid-line",
    descricao: "Limites legais ou técnicos que incidem sobre o exemplar ou a área.",
    categorias: ["Espécie protegida", "APP urbana", "Área tombada", "Rede elétrica — distância mínima", "UC — Unidade de Conservação"],
  },
  {
    tipo: "Autorização",
    icon: "ri-file-shield-2-line",
    descricao: "Atos que permitem uma intervenção, com documento-fonte e prazo de validade.",
    categorias: ["Poda emergencial", "Poda corretiva", "Supressão", "Compensação ambiental", "Transplante"],
  },
];

/** Tipos de evento registrados no historico versionado do exemplar. */
export const ladmEventTypes = [
  "Cadastro inicial",
  "Vistoria técnica",
  "Alteração de risco",
  "Poda",
  "Tratamento fitossanitário",
  "Atualização por laser scanner ou drone",
  "Plantio de muda compensatória",
  "Queda",
  "Supressão autorizada",
  "Transplante autorizado",
  "Replantio",
  "Interferência registrada",
  "Solicitação de vistoria pelo cidadão",
  "Alteração de status",
];

export interface LadmValidationRuleDef {
  regra: string;
  prioridade: "critica" | "alta" | "media" | "baixa";
}

export const ladmValidationRules: LadmValidationRuleDef[] = [
  { regra: "Todo exemplar deve possuir geometria (coordenada)", prioridade: "alta" },
  { regra: "Todo exemplar deve estar vinculado a uma unidade espacial", prioridade: "alta" },
  { regra: "Todo exemplar deve possuir data de cadastro", prioridade: "baixa" },
  { regra: "Toda alteração deve gerar histórico", prioridade: "media" },
  { regra: "Toda informação técnica relevante deve possuir fonte documental", prioridade: "alta" },
  { regra: "Toda supressão deve possuir autorização ou justificativa", prioridade: "critica" },
  { regra: "Todo exemplar de risco alto deve possuir evidência fotográfica", prioridade: "critica" },
  { regra: "Toda nuvem de pontos deve possuir metadados", prioridade: "media" },
  { regra: "Toda intervenção deve possuir responsável vinculado", prioridade: "alta" },
  { regra: "Todo exemplar removido deve permanecer no histórico", prioridade: "alta" },
];

export const ladmPriorityBadge: Record<LadmValidationRuleDef["prioridade"], { label: string; className: string }> = {
  critica: { label: "Crítica", className: "bg-red-100 text-red-700" },
  alta: { label: "Alta", className: "bg-orange-100 text-orange-700" },
  media: { label: "Média", className: "bg-yellow-100 text-yellow-700" },
  baixa: { label: "Baixa", className: "bg-background-100 text-foreground-600" },
};

/** Produtos previstos pela metodologia (sem status nem datas de geracao). */
export const ladmPlannedProducts = [
  { id: "rel-01", title: "Base Cadastral Arbórea Georreferenciada", description: "Base com os exemplares georreferenciados, atributos e metadados" },
  { id: "rel-02", title: "Mapa dos Exemplares Cadastrados", description: "Mapa interativo com camadas de exemplares, áreas verdes, UCs e APPs" },
  { id: "rel-03", title: "Mapa de Risco", description: "Mapa temático com classificação de risco" },
  { id: "rel-04", title: "Mapa de Recomendações de Poda e Manejo", description: "Exemplares agrupados por tipo de manejo recomendado" },
  { id: "rel-05", title: "Mapa de Cobertura Vegetal", description: "Classificação de cobertura vegetal por bairro" },
  { id: "rel-06", title: "Mapa de Espécies", description: "Distribuição das espécies identificadas" },
  { id: "rel-07", title: "Relatório por Bairro", description: "Indicadores por bairro: exemplares, espécies, risco, fitossanidade" },
  { id: "rel-08", title: "Relatório de Responsabilidades", description: "Responsabilidades, restrições e autorizações por unidade espacial" },
  { id: "rel-09", title: "Relatório de Restrições Ambientais", description: "APPs, tombamentos, espécies protegidas, UCs" },
  { id: "rel-10", title: "Relatório de Histórico de Manejo", description: "Linha do tempo das intervenções, vistorias e eventos por exemplar" },
  { id: "rel-11", title: "Relatório Técnico com Fontes e Evidências", description: "Rastreabilidade de cada informação" },
  { id: "rel-12", title: "Dashboard Gerencial", description: "Indicadores metodológicos, qualidade e evolução do inventário" },
  { id: "rel-13", title: "Banco de Imagens", description: "Fotos georreferenciadas por exemplar" },
  { id: "rel-14", title: "Nuvem de Pontos Processada", description: "Nuvem de pontos LiDAR classificada" },
  { id: "rel-15", title: "Modelo 3D da Arborização", description: "Modelo tridimensional com métricas extraídas" },
  { id: "rel-16", title: "Plano Preliminar de Manejo", description: "Plano baseado nas recomendações e prioridades do inventário" },
];
